import { useCallback, useState } from "react";

import Icon from "Components/Icon/Icon";
import usePlan from "hooks/usePlan";
import FeatureBloqueada from "./FeatureBloqueada";
import styles from "./FeatureBloqueada.module.scss";

/**
 * Lo que repite cada disparador bloqueado: interceptar el click y abrir el modal,
 * en vez de esconder el boton (esconderlo esconderia que la feature existe).
 *
 *   const lock = usePlanLock("events.sessions");
 *   <button onClick={lock.guard(onSesiones)}>
 *     <LockedLabel blocked={lock.blocked}>Sesiones</LockedLabel>
 *   </button>
 *   {lock.modal}
 *
 * `blocked` explicito pisa al contrato: es para los LIMITES (techo de agendas),
 * que se comparan contra el uso y no salen de isBlocked().
 * `secondaryAction` ({ label, href }) agrega un link debajo de "Ver planes".
 */
export function usePlanLock(featureKey, { blocked: forced, secondaryAction } = {}) {
  const plan = usePlan();
  const blocked = forced ?? plan.isBlocked(featureKey);
  // El tope sale de la matriz o de un override por team: el copy lo interpola.
  const values = { limit: plan.agendaCap?.value ?? null };
  const [show, setShow] = useState(false);

  const open = useCallback(() => setShow(true), []);
  const close = useCallback(() => setShow(false), []);

  // Envuelve el handler original. Bloqueado: no lo llama, corta el default
  // (links, submits, toggles) y abre el modal. Si no, pasa derecho.
  const guard = useCallback(
    (handler) =>
      (...args) => {
        if (blocked) {
          args[0]?.preventDefault?.();
          args[0]?.stopPropagation?.();
          setShow(true);
          return undefined;
        }
        return handler?.(...args);
      },
    [blocked]
  );

  const modal = (
    <FeatureBloqueada featureKey={featureKey} values={values} show={show} onClose={close} secondaryAction={secondaryAction} />
  );

  return { blocked, guard, open, close, show, modal };
}

// Candado de los disparadores (Figma: lock 16px en #0ca8dd, a opacidad plena
// aunque el texto del boton este atenuado).
export function PlanLockIcon({ size = 16, className = "", testId = "plan-lock-icon" }) {
  return (
    <span data-testid={testId} className={`${styles.triggerLock} ${className}`}>
      <Icon name="lock" size={size} />
    </span>
  );
}

// Texto de un disparador bloqueado: la opacidad va en el texto y no en el boton
// para que el candado quede a color pleno (Figma 3352:22443).
export function LockedLabel({ blocked, children, iconClassName = "ms-1", iconTestId }) {
  return (
    <>
      <span style={{ opacity: blocked ? 0.5 : 1 }}>{children}</span>
      {blocked && <PlanLockIcon className={iconClassName} testId={iconTestId} />}
    </>
  );
}

// 409 del techo de agendas (api: guard.requireCapacity en POST /resources y
// assertAgendaCapacity en teams/save). hooks/useMutation tira `Error(data.error)`
// sin status ni body, asi que lo unico que llega es el codigo en el message.
export const isAgendaCapError = (error) => error?.message === "limit_reached";
